// Lightweight web-analytics wrapper for the public Workload Analyzer (SPD-23855).
//
// Provider: HubSpot (portal 43951636) — the same portal that already runs the
// marketing/registration funnel (speedata.io form + thank-you-page CTAs), so the
// in-app usage funnel joins the same contact timeline instead of living in a
// disconnected second analytics system. See docs/adr-0001-web-analytics.md.
//
// PRIVACY (hard requirement): customer Spark logs never leave the browser. We
// only ever send event names + COARSE, CATEGORICAL props — never file contents,
// filenames, query text, or raw error messages. Every helper below reduces its
// input to a bucket/category before it can reach the provider.

const HUBSPOT_PORTAL_ID = "43951636";
const PROD_HOST = "workloadanalyzer.speedata.io";

// The wamini single-file embed (window.RESULT) or a file:// open must never track.
const MINI = window.RESULT || window.location.href.startsWith("file://");

// Environment split: production domain only. Staging is deployed under /next, and
// localhost / any other host is non-production — exclude all of them.
const isProdHost = window.location.host === PROD_HOST;
const isStagingPath = window.location.pathname.startsWith("/next");
export const ENABLED = isProdHost && !isStagingPath && !MINI;

// HubSpot's event queue — safe to push to even before the loader script arrives;
// it drains the queue once ready.
window._hsq = window._hsq || [];

let loaderInjected = false;

// Inject the standard HubSpot tracking loader, async/deferred so it can never
// block the latency-sensitive Pyodide worker boot.
export function initAnalytics() {
  if (loaderInjected || !ENABLED) return;
  loaderInjected = true;
  const s = document.createElement("script");
  s.type = "text/javascript";
  s.id = "hs-script-loader";
  s.async = true;
  s.defer = true;
  s.src = `//js.hs-scripts.com/${HUBSPOT_PORTAL_ID}.js`;
  document.head.appendChild(s);
}

// --- coarse bucketing (never emit a raw value that could identify a file) ---

export function fileSizeBucket(bytes) {
  if (bytes === undefined || bytes === null || isNaN(bytes)) return "unknown";
  const mb = bytes / (1024 * 1024);
  if (mb < 1) return "<1MB";
  if (mb < 10) return "1-10MB";
  if (mb < 100) return "10-100MB";
  if (mb < 1024) return "100MB-1GB";
  return ">1GB";
}

export function durationBucket(ms) {
  const s = ms / 1000;
  if (s < 5) return "<5s";
  if (s < 30) return "5-30s";
  if (s < 120) return "30-120s";
  if (s < 300) return "2-5m";
  return ">5m";
}

export function queryCountBucket(n) {
  if (n <= 1) return "1";
  if (n <= 5) return "2-5";
  if (n <= 20) return "6-20";
  if (n <= 50) return "21-50";
  return ">50";
}

// Reduce any error to a FIXED category label. Only the label is emitted — the
// original message (which could echo log-derived content) is discarded. The
// keyword matching happens locally and never leaves this function.
export function errorCategory(err) {
  const msg = (typeof err === "string" ? err : (err?.message || err?.error?.message || "")).toLowerCase();
  if (!msg) return "unknown";
  if (msg.includes("unsupported") || msg.includes("not supported") || msg.includes("invalid") || msg.includes("validation")) return "unsupported_log";
  if (msg.includes("parse") || msg.includes("json") || msg.includes("decode")) return "parse_error";
  if (msg.includes("no queries") || msg.includes("no query")) return "no_queries";
  if (msg.includes("worker") || msg.includes("pyodide") || msg.includes("memory")) return "worker_crash";
  return "estimation_error";
}

// --- low-level emit (never throws into the app) ---

function track(name, properties) {
  if (!ENABLED) return;
  try {
    window._hsq.push(["trackCustomBehavioralEvent", { name, properties: properties || {} }]);
  } catch (e) {
    // analytics must never break the app
  }
}

// SPA "screen view": drive a virtual path so the land -> upload -> analyze ->
// results funnel is visible in HubSpot's standard page-view analytics even
// without custom-behavioral-event entitlements. Paths are numbered so the funnel
// ordering is obvious in the dashboard.
export function trackScreen(path) {
  if (!ENABLED) return;
  try {
    window._hsq.push(["setPath", path]);
    window._hsq.push(["trackPageView"]);
  } catch (e) {}
}

export const analytics = {
  screen: trackScreen,
  uploadStarted: (bytes) => track("wa_upload_started", { file_size: fileSizeBucket(bytes) }),
  uploadCompleted: (bytes, fileType) => track("wa_upload_completed", { file_size: fileSizeBucket(bytes), file_type: fileType }),
  analysisStarted: () => track("wa_analysis_started", {}),
  analysisCompleted: (ms, queryCount) => track("wa_analysis_completed", {
    duration: durationBucket(ms),
    query_count: queryCountBucket(queryCount),
    app_version: window.VERSION,
  }),
  analysisFailed: (err) => track("wa_analysis_failed", { error_category: errorCategory(err), app_version: window.VERSION }),
  resultsViewed: (view) => track("wa_results_viewed", { view }),
  configApplied: (comparedType) => track("wa_config_applied", { compared_type: comparedType }),
  webVital: (name, value) => track("wa_web_vital", { metric: name, value: Math.round(value * 1000) / 1000 }),
};
